import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function WorkforceExample({ logo }) {
  useSEO({
    title: 'Workforce Planning Simulation',
    description: 'Simulate project demand against available capacity to right-size teams. Prevent overstaffing and burnout.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Hero Section */}
      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Capacity + Backlog Planning</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Stop overstaffing or burning out your best people
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Simulate project demand against available capacity to right-size teams—before you lose margins or talent.
          </p>
        </div>

        {/* Who is this for */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Practice Leaders</p>
              <p className="text-sm text-gray-600">Balancing utilization with employee experience</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Resource Managers</p>
              <p className="text-sm text-gray-600">Matching skills to projects across time</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">HR & Talent Leaders</p>
              <p className="text-sm text-gray-600">Planning hiring and development investments</p>
            </div>
          </div>
        </div>

        {/* What decisions it improves */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How many people to hire and when</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Which projects to accept given current capacity</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How to balance workloads across practice areas</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Which skills gaps to address through training vs. hiring</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">When bench time becomes a strategic investment</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How project delays cascade through the organization</p>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Example */}
        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Professional Services</p>
            <p className="text-lg leading-relaxed mb-6">
              A consulting firm simulated project backlogs, skill matching requirements, and hiring lead times. 
              The model showed that targeted hiring in one practice area—combined with cross-training in another—would 
              reduce bench time by 25% while preventing burnout-driven turnover in their highest-demand teams.
            </p>
            <p className="text-gray-400 text-sm">
              The impact: $1.8M annual savings from reduced attrition and improved utilization.
            </p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your workforce, test capacity scenarios, and share insights with leadership. Ideal for resource management teams who want ongoing visibility."
          consultingDescription="Partner with our consultants to build a custom workforce model, run planning workshops, and deliver actionable recommendations."
        />

        {/* Related Solutions */}
        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/hospital-or" className="text-gray-700 hover:text-gray-900 text-sm">
              Scheduling + Throughput →
            </Link>
            <Link to="/solutions/revenue-pipeline" className="text-gray-700 hover:text-gray-900 text-sm">
              Pipeline + Revenue Forecasting →
            </Link>
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">
              Inventory + Production →
            </Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default WorkforceExample;
